import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatestWith, finalize, Observable, ReplaySubject} from 'rxjs';
import {first, map, take} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {ManagementStoreService} from './management-store.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {LocationSet} from '@emm/core/models/location-set.model';
import {Node} from '@emm/core/models/node.model';
import {ManagementLocationSetsEffects} from '@emm/management/services/effects/management-location-sets-effects.service';
import {ItemNode} from '@emm/core/util/angular-tree-component.util';


@Injectable({ providedIn: 'root' })
export class LocationSetsFacadeService {

    locationSetDetail$ = new BehaviorSubject<LocationSet | null>(null);
    locationSetsAsNodes$ = new BehaviorSubject<any>(null);
    locationSetTypes$ = new ReplaySubject<any>(null);
    selectedLocationSetType$ = new BehaviorSubject<any>(null);
    allLocations$ = new BehaviorSubject<LocationSet[]>(null);

    centerLoading$ = this.managementStore.isLoading$.locationSetTypes.pipe(
        combineLatestWith(this.managementStore.isLoading$.locationSets),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    rightLoading$ = this.managementStore.isLoading$.base;

    anyLoading$ = this.centerLoading$.pipe(
        combineLatestWith(this.rightLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    constructor(
        private locationSetsEffects: ManagementLocationSetsEffects,
        private managementStore: ManagementStoreService,
        public api: ManagementApiService
    ) {}



    init(): void {
        this.api.getAllLocationSetTypesForEdit().subscribe(locationSetTypes => {
            this.locationSetTypes$.next(locationSetTypes);
        });

        this.api.getAllLocationSetsByTag('LOCATION').subscribe((allLocations) => {
            this.allLocations$.next(allLocations);
        });
    }


    mapLocationSetToNode = (locationSet: LocationSet): ItemNode => {
        return {
            data: {...locationSet},
            hasChildren: true, // level 1 - location sets can have 0 or more child locations
            id: locationSet.tag,
            name: locationSet?.name,
        };
    }

    selectLocationSetType(tag: string): void {
        this.startProgressBar();
        this.api.getLocationSetType(tag).subscribe(selectedLocationSetType =>
            this.selectedLocationSetType$.next(selectedLocationSetType)
        );

        this.selectedLocationSetType$.next(tag)
        this.api.getAllLocationSetsByTag(tag)
            .pipe(
                first(),
                map((locationSets: LocationSet[]) => {
                    const locationSetDetails = RA.isNonEmptyArray(locationSets) ? locationSets.map(this.mapLocationSetToNode) : [];
                    this.locationSetsAsNodes$.next(locationSetDetails);
                    this.locationSetDetail$.next(null);
                }),
                finalize(() => this.stopProgressBar())
            )
            .subscribe();
    }



    getLocationsByParent(id: string): Observable<Node<LocationSet>[]> {
        this.managementStore.dispatch.loadLocationsByParent(id);
        return this.locationSetsEffects.loadLocationsByParent();
    }



    selectLocationSet(locationSet: LocationSet): void {
        this.locationSetDetail$.next(locationSet);
    }



    updateLocationSet(tag: string, updates: any): Observable<LocationSet> {
        return this.api.updateLocationSet(tag, updates).pipe(take(1))
    }



    addChildLocations(parentTag: string, childTags: any): Observable<any> {
        return this.api.addChildLocations(parentTag, childTags);
    }



    deleteLocationSet(tag: string): Observable<any> {
        return this.api.deleteLocationSet(tag);
    }



    removeChildLocation(parentTag: string, childTag: string): any {
        this.api.removeChildLocation(parentTag, childTag).subscribe();
        // return this.managementStore.removeChildLocation$.pipe(take(1));
    }



    reloadLocationSets(tag: string) {
        this.startProgressBar();
        this.api.getAllLocationSetsByTag(tag).pipe(
            first(),
            map((locationSets: LocationSet[]) => {
                const updatedSelectedLocationSet = locationSets.find(set => set.tag === this.locationSetDetail$.getValue()?.tag);
                if (!updatedSelectedLocationSet) {
                    this.locationSetDetail$.next(null);
                } else {
                    this.locationSetDetail$.next(updatedSelectedLocationSet);
                }
                this.locationSetsAsNodes$.next(RA.isNonEmptyArray(locationSets) ? locationSets.map(this.mapLocationSetToNode) : []);
            })
        ).subscribe(() => this.stopProgressBar());
    }

    startProgressBar(): void {
        this.managementStore.dispatch.startLoading();

    }
    stopProgressBar(): void {
        this.managementStore.dispatch.stopLoading();
    }
}
